import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import {
  getComisionesValidadas,
} from "../helpers/rutaComisiones";

import logo from "../images/ridersmiami-logo.png";

/* =========================================================
   FORMATOS
   ========================================================= */

const formatDate = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const m = value.match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

    if (m) {
      return `${m[3]}/${m[2]}/${m[1]}`;
    }
  }

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) {
    return "";
  }

  return d.toLocaleDateString("es-AR");
};

const formatUSD = (value) => {
  const n = Number(value || 0);

  return n.toLocaleString("es-AR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
};

const formatUSDPdf = (value) => {
  const n = Number(value || 0);

  return `US$ ${n.toLocaleString(
    "es-AR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )}`;
};

const cargarImagen = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();

    img.crossOrigin = "Anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const getId = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    return value;
  }

  return value._id || value.id || "";
};

const nombreVendedor = (item) => {
  const vendedor =
    item?.vendedor;

  if (!vendedor) {
    return "-";
  }

  if (
    typeof vendedor === "string"
  ) {
    return vendedor;
  }

  return (
    [
      vendedor.nombres,
      vendedor.apellidos,
    ]
      .filter(Boolean)
      .join(" ") ||
    vendedor.email ||
    "-"
  );
};

const descripcionProducto = (
  item
) => {
  return (
    item?.descripcionProducto ||
    item?.modeloProducto ||
    (item?.codprod
      ? `Producto ${item.codprod}`
      : "-")
  );
};

const textoLiquidada = (
  liquidada
) =>
  liquidada
    ? "Sí"
    : "No";

/* =========================================================
   COMPONENTE
   ========================================================= */

export default function ReporteComisionesDetalle() {
  const [desde, setDesde] =
    useState("");

  const [hasta, setHasta] =
    useState("");

  const [sede, setSede] =
    useState("");

  const [vendedor, setVendedor] =
    useState("");

  const [nroventa, setNroventa] =
    useState("");

  const [liquidada, setLiquidada] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [
    comisiones,
    setComisiones,
  ] = useState([]);

  const [
    vendedoresBase,
    setVendedoresBase,
  ] = useState([]);

  const [resumen, setResumen] =
    useState({
      cantidadRegistros: 0,
      cantidadProductos: 0,
      cantidadVendedores: 0,
      totalComisionUSD: 0,
    });

  /* =======================================================
     VENDEDORES
     ======================================================= */

  const vendedoresDisponibles =
    useMemo(() => {
      const map = new Map();

      vendedoresBase.forEach(
        (item) => {
          const id =
            getId(item?.vendedor);

          const nombre =
            nombreVendedor(item);

          if (
            id &&
            nombre &&
            !map.has(id)
          ) {
            map.set(
              id,
              nombre
            );
          }
        }
      );

      return Array.from(
        map.entries()
      )
        .map(
          ([
            id,
            nombre,
          ]) => ({
            id,
            nombre,
          })
        )
        .sort((a, b) =>
          a.nombre.localeCompare(
            b.nombre,
            "es"
          )
        );
    }, [vendedoresBase]);

  /* =======================================================
     CARGAR
     ======================================================= */

  const cargarReporte = async (
    cargaInicial = false
  ) => {
    setLoading(true);
    setError("");

    try {
      const data =
        await getComisionesValidadas({
          sede,
          vendedor,
          nroventa,
          desde,
          hasta,
          liquidada,
        });

      const registros =
        Array.isArray(
          data?.comisiones
        )
          ? data.comisiones
          : [];

      setComisiones(
        registros
      );

      setResumen({
        cantidadRegistros:
          Number(
            data?.resumen
              ?.cantidadRegistros ??
              registros.length
          ),

        cantidadProductos:
          Number(
            data?.resumen
              ?.cantidadProductos ||
              0
          ),

        cantidadVendedores:
          Number(
            data?.resumen
              ?.cantidadVendedores ||
              0
          ),

        totalComisionUSD:
          Number(
            data?.resumen
              ?.totalComisionUSD ||
              0
          ),
      });

      if (cargaInicial) {
        setVendedoresBase(
          registros
        );
      }
    } catch (err) {
      console.error(
        "Error detalle comisiones:",
        err
      );

      setError(
        err?.response?.data?.error ||
          err?.response?.data
            ?.message ||
          err?.message ||
          "No se pudo cargar el informe."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarReporte(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =======================================================
     LIMPIAR
     ======================================================= */

  const limpiarFiltros = () => {
    setDesde("");
    setHasta("");
    setSede("");
    setVendedor("");
    setNroventa("");
    setLiquidada("");
  };

  /* =======================================================
     CSV
     ======================================================= */

  const generarCSV = () => {
    if (
      comisiones.length === 0
    ) {
      return;
    }

    const encabezados = [
      "Venta",
      "Fecha",
      "Vendedor",
      "Sede",
      "Codigo producto",
      "Producto",
      "Cantidad",
      "Precio lista USD",
      "Precio venta USD",
      "Comision base USD",
      "Comision unitaria USD",
      "Comision total USD",
      "Estado",
      "Liquidada",
    ];

    const escapar = (value) => {
      const texto =
        String(value ?? "");

      return `"${texto.replace(
        /"/g,
        '""'
      )}"`;
    };

    const filas =
      comisiones.map(
        (item) => [
          item?.nroventa || "",

          formatDate(
            item?.fechaVenta
          ),

          nombreVendedor(
            item
          ),

          item?.sede || "",

          item?.codprod || "",

          descripcionProducto(
            item
          ),

          Number(
            item?.cantidad || 0
          ),

          Number(
            item
              ?.precioListaUSD ||
              0
          ),

          Number(
            item
              ?.precioVentaUSD ||
              0
          ),

          Number(
            item
              ?.comisionBaseUSD ||
              0
          ),

          Number(
            item
              ?.comisionUnitarioUSD ||
              0
          ),

          Number(
            item
              ?.comisionTotalUSD ||
              0
          ),

          item?.estado || "",

          textoLiquidada(
            item?.liquidada
          ),
        ]
      );

    const contenido = [
      encabezados,
      ...filas,
    ]
      .map((fila) =>
        fila
          .map(escapar)
          .join(";")
      )
      .join("\n");

    const blob = new Blob(
      [
        "\uFEFF",
        contenido,
      ],
      {
        type:
          "text/csv;charset=utf-8;",
      }
    );

    const url =
      URL.createObjectURL(
        blob
      );

    const link =
      document.createElement(
        "a"
      );

    link.href = url;

    link.download =
      "detalle-comisiones-ventas-riders.csv";

    document.body.appendChild(
      link
    );

    link.click();

    document.body.removeChild(
      link
    );

    URL.revokeObjectURL(
      url
    );
  };

  /* =======================================================
     PDF
     ======================================================= */

  const generarPDF = async () => {
    try {
      const doc =
        new jsPDF(
          "landscape",
          "mm",
          "a4"
        );

      const logoImg =
        await cargarImagen(
          logo
        );

      doc.addImage(
        logoImg,
        "PNG",
        10,
        8,
        34,
        12
      );

      doc.setFont(
        "helvetica",
        "bold"
      );

      doc.setFontSize(16);

      doc.text(
        "Detalle de Comisiones por Venta",
        50,
        14
      );

      doc.setFont(
        "helvetica",
        "normal"
      );

      doc.setFontSize(10);

      doc.text(
        "Riders Miami",
        50,
        20
      );

      doc.text(
        `Desde: ${
          desde || "Todas"
        }   Hasta: ${
          hasta || "Todas"
        }`,
        287,
        14,
        {
          align: "right",
        }
      );

      const vendedorNombre =
        vendedoresDisponibles.find(
          (item) =>
            item.id ===
            vendedor
        )?.nombre ||
        "Todos";

      const liquidadaTexto =
        liquidada ===
        "true"
          ? "Sí"
          : liquidada ===
            "false"
          ? "No"
          : "Todas";

      doc.text(
        `Sede: ${
          sede || "Todas"
        } | Vendedor: ${vendedorNombre} | Venta: ${
          nroventa || "Todas"
        } | Liquidada: ${liquidadaTexto}`,
        287,
        20,
        {
          align: "right",
        }
      );

      doc.setDrawColor(
        180
      );

      doc.line(
        10,
        24,
        287,
        24
      );

      /* RESUMEN */

      const resumenY = 28;
      const boxW = 58;
      const boxH = 14;
      const gap = 4;

      const itemsResumen = [
        {
          label:
            "Registros",
          value:
            String(
              resumen
                .cantidadRegistros ||
                0
            ),
        },
        {
          label:
            "Productos",
          value:
            String(
              resumen
                .cantidadProductos ||
                0
            ),
        },
        {
          label:
            "Vendedores",
          value:
            String(
              resumen
                .cantidadVendedores ||
                0
            ),
        },
        {
          label:
            "Total comisiones",
          value:
            formatUSDPdf(
              resumen
                .totalComisionUSD
            ),
        },
      ];

      itemsResumen.forEach(
        (item, i) => {
          const x =
            10 +
            i *
              (boxW + gap);

          doc.setDrawColor(
            210
          );

          doc.roundedRect(
            x,
            resumenY,
            boxW,
            boxH,
            1.5,
            1.5
          );

          doc.setFont(
            "helvetica",
            "normal"
          );

          doc.setFontSize(8);

          doc.text(
            item.label,
            x + 2,
            resumenY + 4.5
          );

          doc.setFont(
            "helvetica",
            "bold"
          );

          doc.setFontSize(10);

          doc.text(
            String(
              item.value
            ),
            x + 2,
            resumenY + 10
          );
        }
      );

      /* TABLA */

      const body =
        comisiones.map(
          (item) => [
            String(
              item?.nroventa ||
                ""
            ),

            formatDate(
              item?.fechaVenta
            ),

            nombreVendedor(
              item
            ),

            item?.sede || "",

            descripcionProducto(
              item
            ),

            String(
              Number(
                item?.cantidad ||
                  0
              )
            ),

            formatUSDPdf(
              item
                ?.precioVentaUSD
            ),

            formatUSDPdf(
              item
                ?.comisionUnitarioUSD
            ),

            formatUSDPdf(
              item
                ?.comisionTotalUSD
            ),

            item?.estado ===
            "VALIDADA"
              ? "Validada"
              : item?.estado ||
                "-",

            textoLiquidada(
              item?.liquidada
            ),
          ]
        );

      autoTable(doc, {
        startY: 48,

        theme: "grid",

        margin: {
          left: 6,
          right: 6,
        },

        head: [
          [
            "Venta",
            "Fecha",
            "Vendedor",
            "Sede",
            "Producto",
            "Cant.",
            "Precio venta",
            "Com. unitaria",
            "Comisión",
            "Estado",
            "Liquidada",
          ],
        ],

        body,

        foot: [
          [
            "Totales",
            "",
            "",
            "",
            "",
            String(
              resumen
                .cantidadProductos ||
                0
            ),
            "",
            "",
            formatUSDPdf(
              resumen
                .totalComisionUSD
            ),
            "",
            "",
          ],
        ],

        styles: {
          fontSize: 6.7,
          cellPadding: 1,
          valign: "middle",
        },

        headStyles: {
          fillColor: [
            33,
            37,
            41,
          ],
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },

        footStyles: {
          fillColor: [
            245,
            245,
            245,
          ],
          textColor: 20,
          fontStyle: "bold",
        },

        columnStyles: {
          0: {
            cellWidth: 15,
            halign: "center",
          },

          1: {
            cellWidth: 20,
          },

          2: {
            cellWidth: 31,
          },

          3: {
            cellWidth: 20,
          },

          4: {
            cellWidth: 52,
          },

          5: {
            cellWidth: 12,
            halign: "center",
          },

          6: {
            cellWidth: 27,
            halign: "right",
          },

          7: {
            cellWidth: 27,
            halign: "right",
          },

          8: {
            cellWidth: 27,
            halign: "right",
          },

          9: {
            cellWidth: 19,
          },

          10: {
            cellWidth: 18,
            halign: "center",
          },
        },

        didDrawPage: () => {
          const pageCount =
            doc.internal
              .getNumberOfPages();

          doc.setFontSize(8);

          doc.setTextColor(
            100
          );

          doc.text(
            `Página ${pageCount}`,
            doc.internal.pageSize
              .getWidth() - 10,
            doc.internal.pageSize
              .getHeight() - 6,
            {
              align: "right",
            }
          );
        },
      });

      doc.save(
        "detalle-comisiones-ventas-riders.pdf"
      );
    } catch (error) {
      console.error(
        "Error generando PDF:",
        error
      );

      alert(
        "No se pudo generar el PDF."
      );
    }
  };

  /* =======================================================
     VISTA
     ======================================================= */

  return (
    <div className="container-fluid mt-3 mb-4">

      <Card className="shadow-sm border-0">

        <Card.Body>

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">

            <div>
              <h4 className="mb-0">
                Detalle de Comisiones
                por Venta
              </h4>

              <small className="text-muted">
                Riders Miami
              </small>
            </div>

            <div className="d-flex gap-2 flex-wrap">

              <Button
                variant="outline-secondary"
                size="sm"
                onClick={
                  limpiarFiltros
                }
              >
                Limpiar
              </Button>

              <Button
                variant="dark"
                size="sm"
                onClick={() =>
                  cargarReporte(
                    false
                  )
                }
                disabled={loading}
              >
                {loading
                  ? "Buscando..."
                  : "Buscar"}
              </Button>

              <Button
                variant="outline-success"
                size="sm"
                onClick={
                  generarCSV
                }
                disabled={
                  loading ||
                  comisiones.length ===
                    0
                }
              >
                CSV
              </Button>

              <Button
                variant="success"
                size="sm"
                onClick={
                  generarPDF
                }
                disabled={
                  loading ||
                  comisiones.length ===
                    0
                }
              >
                PDF
              </Button>

            </div>
          </div>

          {/* FILTROS */}

          <Row className="g-2 mb-2 align-items-end">

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Desde
              </Form.Label>

              <Form.Control
                type="date"
                value={desde}
                onChange={(e) =>
                  setDesde(
                    e.target.value
                  )
                }
                size="sm"
              />
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Hasta
              </Form.Label>

              <Form.Control
                type="date"
                value={hasta}
                onChange={(e) =>
                  setHasta(
                    e.target.value
                  )
                }
                size="sm"
              />
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Sede
              </Form.Label>

              <Form.Select
                value={sede}
                onChange={(e) =>
                  setSede(
                    e.target.value
                  )
                }
                size="sm"
              >
                <option value="">
                  Todas
                </option>

                <option value="TUCUMAN">
                  Tucumán
                </option>

                <option value="YERBA_BUENA">
                  Yerba Buena
                </option>

                <option value="MIAMI">
                  Miami
                </option>
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Vendedor
              </Form.Label>

              <Form.Select
                value={vendedor}
                onChange={(e) =>
                  setVendedor(
                    e.target.value
                  )
                }
                size="sm"
              >
                <option value="">
                  Todos
                </option>

                {vendedoresDisponibles.map(
                  (item) => (
                    <option
                      key={
                        item.id
                      }
                      value={
                        item.id
                      }
                    >
                      {
                        item.nombre
                      }
                    </option>
                  )
                )}
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Venta
              </Form.Label>

              <Form.Control
                type="number"
                min="1"
                value={nroventa}
                onChange={(e) =>
                  setNroventa(
                    e.target.value
                  )
                }
                placeholder="N° venta"
                size="sm"
              />
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Liquidada
              </Form.Label>

              <Form.Select
                value={liquidada}
                onChange={(e) =>
                  setLiquidada(
                    e.target.value
                  )
                }
                size="sm"
              >
                <option value="">
                  Todas
                </option>

                <option value="true">
                  Sí
                </option>

                <option value="false">
                  No
                </option>
              </Form.Select>
            </Col>

          </Row>

          <div className="small text-muted mb-3">
            Las fechas corresponden
            a la fecha original de la
            venta, no a la fecha en que
            se validó la comisión.
          </div>

          {error && (
            <div className="alert alert-danger py-2">
              {error}
            </div>
          )}

          {/* RESUMEN */}

          <Row className="g-2 mb-3">

            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">

                  <small className="text-muted">
                    Registros
                  </small>

                  <h5 className="mb-0">
                    {
                      resumen
                        .cantidadRegistros
                    }
                  </h5>

                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">

                  <small className="text-muted">
                    Productos
                  </small>

                  <h5 className="mb-0">
                    {
                      resumen
                        .cantidadProductos
                    }
                  </h5>

                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">

                  <small className="text-muted">
                    Vendedores
                  </small>

                  <h5 className="mb-0">
                    {
                      resumen
                        .cantidadVendedores
                    }
                  </h5>

                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">

                  <small className="text-muted">
                    Total comisiones
                  </small>

                  <h5 className="mb-0">
                    {formatUSD(
                      resumen
                        .totalComisionUSD
                    )}
                  </h5>

                </Card.Body>
              </Card>
            </Col>

          </Row>

          {/* TABLA */}

          <div
            className="table-responsive"
            style={{
              maxHeight: "65vh",
            }}
          >

            <Table
              striped
              bordered
              hover
              size="sm"
              className="align-middle"
            >

              <thead
                className="table-dark"
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                }}
              >

                <tr>

                  <th>
                    Venta
                  </th>

                  <th>
                    Fecha
                  </th>

                  <th>
                    Vendedor
                  </th>

                  <th>
                    Sede
                  </th>

                  <th>
                    Producto
                  </th>

                  <th className="text-center">
                    Cant.
                  </th>

                  <th className="text-end">
                    Precio venta
                  </th>

                  <th className="text-end">
                    Comisión unitaria
                  </th>

                  <th className="text-end">
                    Comisión
                  </th>

                  <th className="text-center">
                    Estado
                  </th>

                  <th className="text-center">
                    Liquidada
                  </th>

                </tr>

              </thead>

              <tbody>

                {loading ? (
                  <tr>
                    <td
                      colSpan="11"
                      className="text-center py-4"
                    >
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-2"
                      />

                      Cargando informe...
                    </td>
                  </tr>
                ) : comisiones.length ===
                  0 ? (
                  <tr>
                    <td
                      colSpan="11"
                      className="text-center py-4 text-muted"
                    >
                      No existen comisiones
                      para los filtros
                      seleccionados.
                    </td>
                  </tr>
                ) : (
                  comisiones.map(
                    (item) => (
                      <tr
                        key={
                          item._id
                        }
                      >

                        <td className="fw-semibold">
                          {
                            item
                              .nroventa
                          }
                        </td>

                        <td>
                          {formatDate(
                            item
                              .fechaVenta
                          )}
                        </td>

                        <td>
                          {nombreVendedor(
                            item
                          )}
                        </td>

                        <td>
                          {
                            item.sede
                          }
                        </td>

                        <td>
                          {descripcionProducto(
                            item
                          )}
                        </td>

                        <td className="text-center">
                          {Number(
                            item
                              .cantidad ||
                              0
                          )}
                        </td>

                        <td className="text-end">
                          {formatUSD(
                            item
                              .precioVentaUSD
                          )}
                        </td>

                        <td className="text-end">
                          {formatUSD(
                            item
                              .comisionUnitarioUSD
                          )}
                        </td>

                        <td className="text-end fw-bold">
                          {formatUSD(
                            item
                              .comisionTotalUSD
                          )}
                        </td>

                        <td className="text-center">
                          <Badge bg="success">
                            Validada
                          </Badge>
                        </td>

                        <td className="text-center">
                          <Badge
                            bg={
                              item
                                .liquidada
                                ? "primary"
                                : "warning"
                            }
                          >
                            {item
                              .liquidada
                              ? "Sí"
                              : "No"}
                          </Badge>
                        </td>

                      </tr>
                    )
                  )
                )}

              </tbody>

              {comisiones.length >
                0 && (
                <tfoot>

                  <tr>

                    <th colSpan="5">
                      Totales
                    </th>

                    <th className="text-center">
                      {
                        resumen
                          .cantidadProductos
                      }
                    </th>

                    <th />

                    <th />

                    <th className="text-end">
                      {formatUSD(
                        resumen
                          .totalComisionUSD
                      )}
                    </th>

                    <th colSpan="2" />

                  </tr>

                </tfoot>
              )}

            </Table>
          </div>

        </Card.Body>
      </Card>

    </div>
  );
}